import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import { SkeletonCard } from './components/Skeleton';
import { useCompany } from './contexts/CompanyContext';

interface DashboardProps {
  entrepriseId: string;
}

const Dashboard: React.FC<DashboardProps> = ({ entrepriseId }) => {
  const { exerciceId, entreprises } = useCompany();
  const currentEntreprise = entreprises.find((e) => e.id === entrepriseId);
  const currentExercice = currentEntreprise?.exercices?.find(
    (ex) => ex.id === exerciceId
  );
  const { data: stats, isLoading, error } = useQuery({
    queryKey: ['dashboard', entrepriseId],
    queryFn: () => api.dashboard.get(entrepriseId),
  });

  const { data: alerts } = useQuery({
    queryKey: ['dashboard-alerts', entrepriseId],
    queryFn: () => api.dashboard.getAlerts(entrepriseId),
  });

  const { data: trends } = useQuery({
    queryKey: ['dashboard-monthly', entrepriseId],
    queryFn: () => api.dashboard.getMonthly(entrepriseId),
  });

  const { data: tvaData } = useQuery({
    queryKey: ['dashboard-tva', entrepriseId],
    queryFn: () => api.dashboard.getTva(entrepriseId),
  });

  const { data: fdData } = useQuery({
    queryKey: ['dashboard-fiscal-deadlines'],
    queryFn: () => api.dashboard.getFiscalDeadlines(),
  });

  const fiscalDeadlines = fdData?.deadlines || [];

  const formatCurrency = (centimes: number) => {
    const euros = centimes / 100;
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(euros);
  };

  if (isLoading) return (
    <div className="min-h-screen bg-gray-50">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Tableau de bord</h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <SkeletonCard />
        <SkeletonCard />
      </div>
    </div>
  );
  if (error) return <div className="text-red-700 bg-red-50 border border-red-200 px-4 py-3 rounded-lg text-sm mb-4">Erreur: {(error as Error).message}</div>;
  if (!stats) return null;

  const isSoldePositif = stats.solde_tresorerie_centimes >= 0;
  const isResultatPositif = stats.resultat_exercice_centimes >= 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Tableau de bord</h2>
        {currentExercice && (
          <span className="text-sm text-gray-500 bg-white border border-gray-200 px-3 py-1.5 rounded-lg shadow-sm">
            Exercice&nbsp;:&nbsp;
            <strong className="text-gray-700">
              {new Date(currentExercice.debut).toLocaleDateString('fr-FR')}
              {' '}→{' '}
              {new Date(currentExercice.fin).toLocaleDateString('fr-FR')}
            </strong>
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <div className={`bg-white rounded-xl shadow-sm border p-6 ${isSoldePositif ? 'border-green-200' : 'border-red-200'}`}>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Solde de trésorerie</h3>
          <p className={`text-3xl font-bold ${isSoldePositif ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(stats.solde_tresorerie_centimes)}</p>
          <p className="text-xs text-gray-400 mt-1">Comptes bancaires</p>
        </div>
        <div className={`bg-white rounded-xl shadow-sm border p-6 ${isResultatPositif ? 'border-green-200' : 'border-red-200'}`}>
          <h3 className="text-sm font-medium text-gray-500 mb-1">Résultat de l'exercice</h3>
          <p className={`text-3xl font-bold ${isResultatPositif ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(stats.resultat_exercice_centimes)}</p>
          <p className="text-xs text-gray-400 mt-1">Produits — Charges</p>
        </div>
      </div>

      {tvaData && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">TVA — Aperçu CA12</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h4 className="text-sm font-medium text-gray-500 mb-1">TVA collectée (44571)</h4>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(tvaData.tva_collectee_centimes)}</p>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <h4 className="text-sm font-medium text-gray-500 mb-1">TVA déductible (44566+44562)</h4>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(tvaData.tva_deductible_centimes)}</p>
            </div>
            <div className={`bg-white rounded-xl shadow-sm border p-5 ${tvaData.tva_nette_centimes >= 0 ? 'border-green-200' : 'border-red-200'}`}>
              <h4 className="text-sm font-medium text-gray-500 mb-1">TVA nette</h4>
              <p className={`text-2xl font-bold ${tvaData.tva_nette_centimes >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(tvaData.tva_nette_centimes)}</p>
              <p className="text-xs text-gray-400 mt-1">{tvaData.tva_nette_centimes >= 0 ? 'À reverser' : 'Crédit de TVA'}</p>
            </div>
          </div>
          {tvaData.monthly.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Détail mensuel</h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Mois</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Collectée</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Déductible</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Nette</th>
                  </tr>
                </thead>
                <tbody>
                  {tvaData.monthly.map((m: any) => (
                    <tr key={m.month} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{m.month}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(m.collectee_centimes)}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(m.deductible_centimes)}</td>
                      <td className={`px-4 py-3 border-b border-gray-100 text-right font-medium ${m.nette_centimes >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(m.nette_centimes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {fiscalDeadlines.length > 0 && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Échéances fiscales</h3>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Échéance</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Jours restants</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Statut</th>
                </tr>
              </thead>
              <tbody>
                {fiscalDeadlines.map((d: any, i: number) => (
                  <tr key={i} className={`transition-colors ${d.urgent ? 'bg-red-50' : d.daysUntil < 0 ? 'bg-gray-50' : 'hover:bg-gray-50'}`}>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{d.label}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{d.date}</td>
                    <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{d.daysUntil > 0 ? d.daysUntil : d.daysUntil === 0 ? "Aujourd'hui" : 'Passée'}</td>
                    <td className="px-4 py-3 border-b border-gray-100">{d.urgent && d.daysUntil >= 0 ? <span className="text-red-600 font-bold text-xs">URGENT</span> : d.daysUntil < 0 ? <span className="text-gray-400 text-xs">Terminée</span> : <span className="text-green-600 text-xs">À venir</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {alerts && (
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Alertes et activités récentes</h3>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-4">
            <ul className="space-y-2 text-sm text-gray-700">
              {alerts.fiscalDeadline && <li>Clôture de l'exercice: <strong>{alerts.fiscalDeadline}</strong></li>}
              <li>Transactions non catégorisées: <strong className="text-amber-600">{alerts.uncategorizedCount}</strong></li>
            </ul>
          </div>

          <h4 className="text-sm font-semibold text-gray-700 mb-3">Dernières transactions</h4>
          {alerts.recentTransactions.length === 0 ? <p className="text-sm text-gray-500">Aucune transaction.</p> : (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-6">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Libellé</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Montant TTC</th>
                  </tr>
                </thead>
                <tbody>
                  {alerts.recentTransactions.map((tx: any) => (
                    <tr key={tx.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{tx.date}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100">{tx.libelle}</td>
                      <td className="px-4 py-3 text-gray-700 border-b border-gray-100 text-right">{formatCurrency(tx.total_ttc_centimes)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {trends && trends.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Recettes vs Dépenses (12 mois)</h3>
              {(() => {
                const max = Math.max(...trends.map((t: any) => Math.max(t.recette_centimes, t.depense_centimes)), 1);
                return (
                  <div className="space-y-2">
                    {trends.map((m: any) => (
                      <div key={m.month} className="flex items-center gap-3">
                        <div className="w-10 text-xs text-gray-500 text-right shrink-0">{m.month.slice(5)}</div>
                        <div className="flex-1 space-y-1">
                          <div className="h-3 bg-green-400 rounded-sm" style={{ width: `${(m.recette_centimes / max) * 100}%` }} title={`Recettes: ${formatCurrency(m.recette_centimes)}`}></div>
                          <div className="h-3 bg-red-400 rounded-sm" style={{ width: `${(m.depense_centimes / max) * 100}%` }} title={`Dépenses: ${formatCurrency(m.depense_centimes)}`}></div>
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div className="flex gap-4 mt-4 text-xs text-gray-500">
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded-sm inline-block"></span>Recettes</span>
                <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded-sm inline-block"></span>Dépenses</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
